/* ==========================================================
   Modish custom sections — shared behaviour (Andrew's)
   Loaded as a module once; safe to include from every section.
   ========================================================== */
if (!window.__modishLoaded) {
  window.__modishLoaded = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const WL_KEY = 'modish-wishlist';

  /* ---------- Horizon cart events (standard events) ---------- */
  let Events = null;
  const eventsReady = import('@shopify/events')
    .then((m) => { Events = m; })
    .catch(() => { Events = null; });

  /* ---------- Toast ---------- */
  let toastEl, toastTimer;
  function toast(html) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'm-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = html;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 4000);
  }

  function drawerIsOpen() {
    return !!document.querySelector('cart-drawer-component theme-drawer[open], cart-drawer-component [open], theme-drawer[open]');
  }

  /* ---------- Add to cart ---------- */
  async function addLines(items, sourceEl) {
    await eventsReady;
    const CLU = Events && Events.CartLinesUpdateEvent;
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    let deferred = null;
    if (CLU) {
      deferred = CLU.createPromise();
      (sourceEl || document.body).dispatchEvent(
        new CLU({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: Number(i.quantity) || 1 })),
          promise: deferred.promise,
        })
      );
    }

    const body = { items: items.map((i) => ({ id: Number(i.id), quantity: Number(i.quantity) || 1 })) };
    if (sectionIds.length) body.sections = sectionIds.join(',');

    let res, data, cart;
    try {
      res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      data = await res.json();
      cart = await (await fetch(root + 'cart.js')).json();
    } catch (e) {
      if (deferred) deferred.reject(e);
      throw e;
    }

    const failed = !res.ok || data.status;
    if (deferred) {
      deferred.resolve({
        cart: CLU.createCartFromAjaxResponse(cart),
        detail: {
          didError: !!failed,
          items: cart.items,
          source: 'modish',
          sourceId: 'modish',
          itemCount: body.items.reduce((n, i) => n + i.quantity, 0),
          sections: failed ? undefined : data.sections,
        },
      });
    }
    if (failed) throw new Error(data.description || data.message || 'Could not add to bag');

    // Fallback confirmation if no drawer opened (drawer off, or older theme)
    setTimeout(() => {
      if (!drawerIsOpen()) {
        toast(`<span>Added to bag</span><a href="${root}cart">View bag</a>`);
        if (!CLU) document.querySelectorAll('.cart-bubble__text-count, [data-cart-count]').forEach((el) => (el.textContent = cart.item_count));
      }
    }, 700);
    return cart;
  }
  window.Modish = Object.assign(window.Modish || {}, { addLines, toast });

  /* ---------- Money ---------- */
  function money(cents) {
    const fmt = window.ModishMoneyFormat || '{{amount}}';
    const n = cents / 100;
    const two = (v, dec, th, ds) => {
      const parts = v.toFixed(dec).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return parts.join(ds);
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
      switch (k) {
        case 'amount_no_decimals': return two(n, 0, ',', '.');
        case 'amount_with_comma_separator': return two(n, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return two(n, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return two(n, 2, "'", '.');
        default: return two(n, 2, ',', '.');
      }
    });
  }

  /* ---------- Wishlist ---------- */
  const wl = {
    get() { try { return JSON.parse(localStorage.getItem(WL_KEY)) || []; } catch (e) { return []; } },
    set(list) { try { localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch (e) {} wl.paint(); },
    has(h) { return wl.get().includes(h); },
    toggle(h) {
      const list = wl.get();
      const i = list.indexOf(h);
      if (i > -1) list.splice(i, 1); else list.unshift(h);
      wl.set(list);
      return i === -1;
    },
    paint() {
      const list = wl.get();
      document.querySelectorAll('[data-m-wish]').forEach((b) => {
        const on = list.includes(b.dataset.mWish);
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      document.querySelectorAll('[data-m-wish-count]').forEach((el) => {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
    },
  };

  /* ---------- Variant helpers ---------- */
  function findVariant(variants, selected) {
    return variants.find((v) => v.options.every((o, i) => o === selected[i]));
  }

  /* ---------- Product page ---------- */
  function initProduct(el) {
    if (el.__mInit) return; el.__mInit = true;
    const data = JSON.parse(el.querySelector('[data-m-product-json]').textContent);
    const form = el.querySelector('form[data-m-product-form]');
    const idInput = form.querySelector('input[name="id"]');
    const atc = el.querySelectorAll('[data-m-atc]');
    const msg = el.querySelector('[data-m-msg]');
    const notice = el.querySelector('[data-m-notice]');
    const priceEl = el.querySelector('[data-m-price]');
    const sticky = el.querySelector('.m-sticky-atc');

    function selected() {
      return data.options.map((_, i) => {
        const c = el.querySelector(`input[name="m-opt-${i}"]:checked`);
        return c ? c.value : null;
      });
    }

    function markAvailability() {
      const sel = selected();
      data.options.forEach((_, i) => {
        el.querySelectorAll(`input[name="m-opt-${i}"]`).forEach((inp) => {
          const test = sel.slice(); test[i] = inp.value;
          const v = findVariant(data.variants, test);
          inp.classList.toggle('is-out', !v || !v.available);
        });
      });
    }

    function update(pushUrl) {
      const sel = selected();
      sel.forEach((val, i) => {
        const lab = el.querySelector(`[data-m-opt-label="${i}"]`);
        if (lab) lab.textContent = val || '';
      });
      const v = findVariant(data.variants, sel);
      markAvailability();
      if (!v) {
        atc.forEach((b) => { b.disabled = true; b.textContent = b.dataset.unavailable; });
        return;
      }
      idInput.value = v.id;
      atc.forEach((b) => {
        b.disabled = !v.available;
        b.textContent = v.available ? b.dataset.add + (b.dataset.withPrice ? ', ' + money(v.price) : '') : b.dataset.soldout;
      });
      if (notice) {
        notice.hidden = v.available;
        const n = notice.querySelector('[data-m-notice-size]');
        if (n) n.textContent = v.title;
      }
      el.querySelectorAll('[data-m-bis-variant]').forEach((i) => (i.value = `${data.title} — ${v.title} (variant ${v.id})`));
      el.querySelectorAll('[data-m-bis-label]').forEach((i) => (i.textContent = v.title));
      if (priceEl) {
        const sale = v.compare_at_price && v.compare_at_price > v.price;
        priceEl.innerHTML = sale
          ? `<span class="is-sale">${money(v.price)}</span><s>${money(v.compare_at_price)}</s><span class="m-pdp__save">${priceEl.dataset.saveLabel} ${Math.round((1 - v.price / v.compare_at_price) * 100)}%</span>`
          : `<span>${money(v.price)}</span>`;
      }
      if (v.featured_media && pushUrl) showMedia(v.featured_media.id);
      if (pushUrl && history.replaceState) {
        const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState({}, '', u);
      }
    }

    function showMedia(id) {
      const target = el.querySelector(`[data-m-media="${id}"]`);
      if (!target) return;
      el.querySelectorAll('[data-m-thumb]').forEach((t) => t.classList.toggle('is-active', t.dataset.mThumb == id));
      if (window.matchMedia('(max-width: 989px)').matches) {
        target.parentElement.scrollTo({ left: target.offsetLeft - target.parentElement.offsetLeft, behavior: 'smooth' });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    el.querySelectorAll('[data-m-thumb]').forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.mThumb)));
    el.addEventListener('change', (e) => { if (e.target.name && e.target.name.startsWith('m-opt-')) update(true); });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const qty = form.querySelector('[name="quantity"]');
      atc.forEach((b) => b.setAttribute('aria-busy', 'true'));
      msg.textContent = '';
      try {
        await addLines([{ id: idInput.value, quantity: qty ? qty.value : 1 }], form);
      } catch (err) {
        msg.textContent = err.message;
      } finally {
        atc.forEach((b) => b.removeAttribute('aria-busy'));
      }
    });

    if (sticky) {
      const mainBtn = form.querySelector('[data-m-atc]');
      sticky.querySelector('[data-m-atc]').addEventListener('click', () => form.requestSubmit());
      new IntersectionObserver(([en]) => sticky.classList.toggle('is-visible', !en.isIntersecting && en.boundingClientRect.top < 0))
        .observe(mainBtn);
    }
    update(false);
  }

  /* ---------- Shop the look ---------- */
  function initLook(el) {
    if (el.__mInit) return; el.__mInit = true;
    el.querySelectorAll('[data-m-dot]').forEach((d) =>
      d.addEventListener('click', () => {
        const item = el.querySelector(`[data-m-look-item="${d.dataset.mDot}"]`);
        if (!item) return;
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        item.classList.add('is-flash');
        setTimeout(() => item.classList.remove('is-flash'), 900);
        const s = item.querySelector('select'); if (s) s.focus();
      })
    );
    const btn = el.querySelector('[data-m-look-add]');
    const msg = el.querySelector('[data-m-look-msg]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const items = []; let missing = false;
      el.querySelectorAll('[data-m-look-item]').forEach((it) => {
        const s = it.querySelector('select');
        const id = s ? s.value : it.dataset.variant;
        if (!id) missing = true; else items.push({ id, quantity: 1 });
      });
      if (missing) { msg.textContent = btn.dataset.missing; return; }
      msg.textContent = '';
      btn.disabled = true;
      try { await addLines(items, el); } catch (e) { msg.textContent = e.message; }
      btn.disabled = false;
    });
  }

  /* ---------- Wishlist page ---------- */
  async function initWishlistPage(el) {
    if (el.__mInit) return; el.__mInit = true;
    const grid = el.querySelector('[data-m-wishlist-grid]');
    const empty = el.querySelector('[data-m-wishlist-empty]');
    const actions = el.querySelector('[data-m-wishlist-actions]');
    const count = el.querySelector('[data-m-wishlist-count]');
    const shared = new URL(location.href).searchParams.get('items');
    const handles = shared ? shared.split(',').filter(Boolean) : wl.get();
    const products = (await Promise.all(
      handles.map((h) => fetch(`${root}products/${h}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null))
    )).filter(Boolean);

    const render = () => {
      const list = shared ? products : products.filter((p) => wl.has(p.handle));
      if (count) count.textContent = list.length;
      empty.hidden = list.length > 0;
      actions.hidden = list.length === 0;
      grid.innerHTML = list.map((p) => {
        const sale = p.compare_at_price && p.compare_at_price > p.price;
        const img = p.featured_image ? `${p.featured_image}${p.featured_image.includes('?') ? '&' : '?'}width=600` : '';
        const tag = !p.available ? `<span class="m-card__tag">${el.dataset.soldout}</span>` : sale ? `<span class="m-card__tag m-card__tag--sale">${el.dataset.sale}</span>` : '';
        return `<div class="m-card ${p.available ? '' : 'is-soldout'}">
          <div class="m-card__media">${img ? `<img src="${img}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">` : ''}${tag}
          <button type="button" class="m-heart is-on" data-m-wish="${p.handle}" aria-label="${el.dataset.remove}"><svg viewBox="0 0 24 24"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z"/></svg></button></div>
          <div class="m-card__meta"><a class="m-card__title" href="${p.url}">${p.title}</a>
          <div class="m-price">${sale ? `<s>${money(p.compare_at_price)}</s><span class="is-sale">${money(p.price)}</span>` : `<span>${money(p.price)}</span>`}</div></div></div>`;
      }).join('');
      wl.paint();
    };
    render();
    if (!shared) document.addEventListener('modish:wishlist', render);

    el.querySelector('[data-m-wishlist-share]')?.addEventListener('click', async () => {
      const u = new URL(location.href); u.searchParams.set('items', wl.get().join(','));
      try {
        if (navigator.share) await navigator.share({ url: u.toString() });
        else { await navigator.clipboard.writeText(u.toString()); toast(el.dataset.copied); }
      } catch (e) {}
    });
    el.querySelector('[data-m-wishlist-addall]')?.addEventListener('click', async () => {
      const items = products
        .filter((p) => (shared || wl.has(p.handle)) && p.available)
        .map((p) => ({ id: (p.variants.find((v) => v.available) || {}).id, quantity: 1 }))
        .filter((i) => i.id);
      if (!items.length) return;
      try { await addLines(items, el); } catch (e) { toast(e.message); }
    });
  }

  /* ---------- Lazy section fetch (recommendations) ---------- */
  async function initReco(el) {
    if (el.__mInit) return; el.__mInit = true;
    try {
      const html = await (await fetch(el.dataset.mReco)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-m-reco-target]');
      const target = el.querySelector('[data-m-reco-target]');
      if (fresh && fresh.children.length && target) { target.innerHTML = fresh.innerHTML; el.hidden = false; wl.paint(); }
      else el.hidden = true;
    } catch (e) { el.hidden = true; }
  }

  /* ---------- Collection: load more + filters ---------- */
  function initCollection(el) {
    if (el.__mInit) return; el.__mInit = true;
    const form = el.querySelector('[data-m-filter-form]');
    if (form) {
      form.addEventListener('change', (e) => {
        if (e.target.matches('input[type="number"]')) return;
        form.requestSubmit ? form.requestSubmit() : form.submit();
      });
      form.addEventListener('submit', (e) => {
        // Strip empty price fields so the URL stays clean
        form.querySelectorAll('input[type="number"]').forEach((i) => { if (!i.value) i.disabled = true; });
      });
    }
    document.addEventListener('click', (e) => {
      el.querySelectorAll('.m-filter[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
    const more = el.querySelector('[data-m-more]');
    if (more) more.addEventListener('click', async () => {
      more.disabled = true;
      const html = await (await fetch(more.dataset.mMore)).text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const items = doc.querySelectorAll('[data-m-grid] > *');
      const grid = el.querySelector('[data-m-grid]');
      items.forEach((i) => grid.appendChild(i));
      const nextBtn = doc.querySelector('[data-m-more]');
      const prog = doc.querySelector('[data-m-progress]');
      if (prog) el.querySelector('[data-m-progress]').innerHTML = prog.innerHTML;
      if (nextBtn) { more.dataset.mMore = nextBtn.dataset.mMore; more.disabled = false; }
      else more.remove();
      wl.paint();
    });
  }

  /* ---------- Global click delegation ---------- */
  document.addEventListener('click', async (e) => {
    const wish = e.target.closest('[data-m-wish]');
    if (wish) {
      e.preventDefault();
      const added = wl.toggle(wish.dataset.mWish);
      document.dispatchEvent(new CustomEvent('modish:wishlist'));
      toast(added ? `<span>Saved to wishlist</span><a href="${window.ModishWishlistUrl || root + 'pages/wishlist'}">View</a>` : '<span>Removed from wishlist</span>');
      return;
    }
    const add = e.target.closest('[data-m-add]');
    if (add) {
      e.preventDefault();
      add.disabled = true;
      try { await addLines([{ id: add.dataset.mAdd, quantity: 1 }], add); } catch (err) { toast(err.message); }
      add.disabled = false;
      return;
    }
    const opener = e.target.closest('[data-m-open]');
    if (opener) {
      const d = document.getElementById(opener.dataset.mOpen);
      if (d && d.showModal) { e.preventDefault(); d.showModal(); }
      return;
    }
    const closer = e.target.closest('[data-m-close]');
    if (closer) { closer.closest('dialog')?.close(); return; }
    if (e.target.matches('dialog.m-dialog')) { e.target.close(); return; } // backdrop click

    const tab = e.target.closest('[data-m-tab]');
    if (tab) {
      const box = tab.closest('[data-m-tabs]');
      box.querySelectorAll('[data-m-tab]').forEach((t) => t.setAttribute('aria-selected', t === tab ? 'true' : 'false'));
      box.querySelectorAll('[data-m-panel]').forEach((p) => (p.hidden = p.dataset.mPanel !== tab.dataset.mTab));
      return;
    }
    const uf = e.target.closest('[data-m-ugc-filter]');
    if (uf) {
      const sec = uf.closest('[data-m-ugc-section]');
      sec.querySelectorAll('[data-m-ugc-filter]').forEach((c) => c.classList.toggle('is-active', c === uf));
      sec.querySelectorAll('[data-m-ugc-cat]').forEach((t) => (t.hidden = !!uf.dataset.mUgcFilter && t.dataset.mUgcCat !== uf.dataset.mUgcFilter));
      return;
    }
    const ugc = e.target.closest('[data-m-ugc]');
    if (ugc) {
      const tpl = ugc.querySelector('template');
      const dlg = document.getElementById(ugc.dataset.mUgc);
      if (!tpl || !dlg) return;
      e.preventDefault();
      dlg.querySelector('[data-m-ugc-body]').innerHTML = tpl.innerHTML;
      dlg.showModal();
      dlg.querySelector('video')?.play().catch(() => {});
      wl.paint();
    }
  });

  document.addEventListener('close', (e) => {
    if (e.target.matches && e.target.matches('[data-m-ugc-dialog]')) e.target.querySelector('video')?.pause();
  }, true);

  /* ---------- Init (and re-init inside the theme editor) ---------- */
  function initAll(scope = document) {
    scope.querySelectorAll('[data-m-product]').forEach(initProduct);
    scope.querySelectorAll('[data-m-look]').forEach(initLook);
    scope.querySelectorAll('[data-m-wishlist]').forEach(initWishlistPage);
    scope.querySelectorAll('[data-m-reco]').forEach(initReco);
    scope.querySelectorAll('[data-m-collection]').forEach(initCollection);
    wl.paint();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
}
